import { useLingui } from '@lingui/react/macro';
import { Check, Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { z } from 'zod';

import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { COLUMN_PRESETS, ColumnValue, columnOptionsSchema, readPath, resolvePatternText, textSchema, type ColumnSpec } from '@/shared/patterns';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

export interface DescriptionItem {
  readonly id: string;
  readonly label: ReactNode;
  readonly value: ReactNode;
  /** Colunas ocupadas na grade da web. */
  readonly span?: 1 | 2 | 3 | undefined;
  /** Texto copiável (documento, chave): aparece o botão de copiar (U39 §11). */
  readonly copy?: string | undefined;
}

export interface DescriptionListProps {
  readonly items: readonly DescriptionItem[];
  /** Colunas na largura grande; abaixo de 360 px de container é sempre uma. */
  readonly columns?: 1 | 2 | 3;
  /** iPhone: rótulo em cima, valor embaixo, uma coluna (protótipo `m_detail`). */
  readonly compact?: boolean;
  readonly className?: string;
}

const SPAN = { 1: '', 2: '@min-[360px]/dl:col-span-2', 3: '@min-[360px]/dl:col-span-2 @min-[720px]/dl:col-span-3' } as const;
const COLUMNS = { 1: '', 2: '@min-[360px]/dl:grid-cols-2', 3: '@min-[360px]/dl:grid-cols-2 @min-[720px]/dl:grid-cols-3' } as const;

function CopyButton({ value, label }: { readonly value: string; readonly label: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground"
      aria-label={copied ? t`Copiado` : t`Copiar ${label}`}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
    </Button>
  );
}

/**
 * Lista de rótulo e valor (P23): resumos, cartões de detalhe, dados de aprovação. Grade por
 * container query na web (1, 2 ou 3 colunas) e lista empilhada no iPhone.
 */
export function DescriptionList({ items, columns = 2, compact = false, className }: DescriptionListProps) {
  return (
    <div className={cn('@container/dl', className)}>
      <dl data-slot="description-list" className={cn('grid gap-x-6', compact ? 'grid-cols-1 gap-y-3' : cn('grid-cols-1 gap-y-4', COLUMNS[columns]))}>
        {items.map((item) => (
          <div key={item.id} className={cn('flex min-w-0 flex-col gap-0.5', !compact && SPAN[item.span ?? 1])}>
            <dt className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-xs md:text-sm')}>{item.label}</dt>
            <dd className={cn('flex min-w-0 items-center gap-1 font-medium break-words text-foreground', compact ? 'text-sm' : 'text-sm md:text-base')}>
              <span className="min-w-0">{item.value}</span>
              {item.copy ? <CopyButton value={item.copy} label={typeof item.label === 'string' ? item.label : item.copy} /> : null}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ─── genérico de bloco ──────────────────────────────────────────────────────

/** Um par rótulo/valor com o preset de coluna (os mesmos formatos da lista, sem repetir código). */
export const descriptionItemSchema = z.object({
  field: z.string().min(1),
  label: textSchema,
  preset: z.enum(COLUMN_PRESETS),
  options: columnOptionsSchema.optional(),
  span: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  copy: z.boolean().optional(),
});

export const descriptionListBlockProps = z.object({
  items: z.array(descriptionItemSchema).min(1).max(24),
  columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
});
export type DescriptionListBlockProps = z.infer<typeof descriptionListBlockProps>;
type DescriptionView = BlockComponentProps<DescriptionListBlockProps, object>;

function useItems(props: DescriptionListBlockProps, data: object): DescriptionItem[] {
  const { i18n } = useLingui();
  return props.items.map((item) => {
    const column: ColumnSpec = { field: item.field, label: item.label, preset: item.preset, ...(item.options ? { options: item.options } : {}) };
    const raw = readPath(data, item.field);
    return {
      id: item.field,
      label: resolvePatternText(i18n, item.label),
      value: <ColumnValue column={column} row={data} />,
      span: item.span,
      copy: item.copy === true && (typeof raw === 'string' || typeof raw === 'number') ? String(raw) : undefined,
    };
  });
}

function DescriptionListBlockWeb({ props, data }: DescriptionView) {
  return <DescriptionList items={useItems(props, data)} columns={props.columns ?? 2} />;
}

function DescriptionListBlockMobile({ props, data }: DescriptionView) {
  return <DescriptionList compact items={useItems(props, data)} />;
}

/** `DescriptionList` como genérico do catálogo, sobre uma fonte `record`. */
export const DescriptionListBlock = defineBlockComponent<DescriptionListBlockProps, object>({
  id: 'DescriptionList',
  description: 'Label and value pairs of one record, each value formatted by a column preset (money, document, date, status…).',
  props: descriptionListBlockProps,
  render: { web: DescriptionListBlockWeb, mobile: DescriptionListBlockMobile },
});
